import { Box, Card, CardActions, CardContent, Stack, Typography } from "@mui/material";
import { ModuleProgressIndicator } from "../utils/ModuleProgressIndicator";
import { useHistory } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { ComputedQuizScore, CourseStructureCtx, UserCtx } from "../../../interfaces";
import { ButtonZoom } from "../utils/ButtonZoom";
import { makeStyles } from "@mui/styles";
import { useContext, useMemo } from "react";
import { CourseStructureContext } from "../../../context/CourseStructureContext";
import { UserContext } from "../../../context/UserContext";
import { Target } from "../../../components/utils/CourseTree";
import { generateGtmEvent, scrubHTML } from "../../../Helpers";

const useStyle = makeStyles((theme) => ({
  percentageStatus: {
    width: "66%",
    fontWeight: 600,
    fontSize: "1em",
    color: "#787676",
    letterSpacing: "0.15px",
    display: "block",
  },
}));

interface ModuleSummaryCardProps {
  moduleId: string;
  moduleTitle: string;
  moduleText: string;
  targetText?: string;
  progress: number;
  resumeQuestionId: string | undefined;
  isDemo?: boolean;
}

export const ModuleSummaryCard = (props: ModuleSummaryCardProps) => {
  const classes = useStyle();
  const history = useHistory();
  const courseStructureCtx = useContext(CourseStructureContext) as CourseStructureCtx;
  const userCtx = useContext(UserContext) as UserCtx;
  const courseMetadata =
    courseStructureCtx.courseTreeStructure?.getCurrentCourseMetadata();
  const languageOverrides = courseMetadata?.language_overrides;
  const targetLangOverrides = languageOverrides?.target;

  const handleButtonClick = (text: string) => {
    generateGtmEvent({
      event: "link",
      action: "click",
      name: "onclick",
      type: "internal link",
      region: "main content",
      section: "overview",
      text: text.toLowerCase(),
    });
    courseStructureCtx.setBackBehavior({ backTo: "moduleoverview" });
    history.push({
      pathname: "/solver",
      search: "&questionId=" + props.resumeQuestionId,
    });
  };

  const sanitizedModuleText = useMemo(() => {
    return scrubHTML(props.moduleText, ["b", "i"]);
  }, [props.moduleText]);

  let currentModule = courseStructureCtx.courseTreeStructure?.getModule(props.moduleId);

  const moduleProgress = useMemo<ComputedQuizScore>(() => {
    const targets = currentModule?.children as Target[];
    return userCtx.calculateLearningMapProgress(targets);
  }, [currentModule, userCtx]);

  return (
    <Card
      sx={{
        padding: "2rem",
        minWidth: "338px",
      }}>
      <CardContent
        role="region"
        aria-labelledby="module-overview-heading"
        tabIndex={0}
        sx={{
          padding: "0px !important",
          display: "flex",
          flexDirection: "column",
          alignItems: "left",
        }}>
        <Typography
          id="module-overview-heading"
          sx={{
            fontSize: "28px",
            fontWeight: "bold",
          }}
          variant="h3">
          Overview
        </Typography>
        <Box
          sx={{
            margin: "32px 0 32px 0",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          }}>
          {courseMetadata?.module_overview_page?.progress_display_type === "score" ? (
            <Stack spacing={0.5}>
              <Typography
                sx={{
                  fontSize: "24px",
                  fontWeight: "600",
                  lineHeight: "24px",
                }}
                variant="h4">
                {moduleProgress.points}/{moduleProgress.max_points}
              </Typography>
              <Typography
                variant="h5"
                component="span"
                sx={{
                  fontWeight: 600,
                  fontSize: "1em",
                  color: "#5D5D5D",
                  letterSpacing: "0.15px",
                }}>
                Questions Attempted
              </Typography>
            </Stack>
          ) : (
            <ModuleProgressIndicator
              moduleId={props.moduleId}
              percentage={props.progress}
              moduleName={props.moduleTitle}
              progressBarSx={{ width: "100%" }}
              style={classes.percentageStatus}
            />
          )}
        </Box>
        <Typography
          sx={{
            marginBottom: "0.75rem",
          }}
          variant="body1"
          dangerouslySetInnerHTML={{
            __html: sanitizedModuleText,
          }}></Typography>
        {/* Removed per ticket 5405 */}
        {!courseMetadata?.module_overview_page?.disable_click_to_start_paragraph && (
          <Typography
            sx={{
              marginBottom: "0.75rem",
            }}
            variant="body1">
            {props.targetText ??
              `In each ${
                targetLangOverrides?.singular?.toLowerCase() ?? "learning target"
              }, you’ll practice one piece of the module to build more
          knowledge and understanding of math concepts.`}
          </Typography>
        )}
        {!courseMetadata?.module_overview_page?.disable_continue_progress_paragraph && (
          <Typography variant="body1">
            {`Start from where you left off, or begin a new ${
              targetLangOverrides?.singular?.toLowerCase() ?? "learning target"
            } by clicking Start
          on the one that you wish to begin.`}
          </Typography>
        )}
      </CardContent>
      {props.resumeQuestionId && (
        <CardActions sx={{ height: "70px", margin: "24px 0px -2px -8px" }}>
          <ButtonZoom
            size="large"
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            color="secondary"
            onClick={() => {
              props.progress === 0
                ? handleButtonClick("Start module")
                : handleButtonClick("Resume learning");
            }}>
            {props.progress === 0
              ? `Start ${
                  courseMetadata?.language_overrides?.module?.singular?.toLowerCase() ??
                  "module"
                }`
              : "Resume learning"}
          </ButtonZoom>
        </CardActions>
      )}
    </Card>
  );
};
